/**
 * GuidedTour — calm overlay that walks first-time visitors through the demo.
 *
 * 12-step tour, role-adapted (parent / caregiver / admin show different paths).
 * Each step has a title + body + (optional) navigate-on-next destination so the
 * tour itself moves the user across the app.
 *
 * Visual model: a soft frosted backdrop + a single floating card. We do NOT
 * try to highlight specific elements with a spotlight — that's a maintenance
 * trap as the UI evolves. The card is friendly prose with arrow buttons.
 *
 * Dismissed-state persists to localStorage so the tour doesn't reopen.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const COMMON_STEPS = [
  {
    title: "Welcome to the live demo.",
    body: "You're looking at Alouvya for a fictional center — Maple Grove Daycare. Every screen, every photo, every message is fake data. Click around freely.",
  },
  {
    title: "Switch persona anytime.",
    body: "The bar at the top lets you see the same day as a parent, a caregiver, or an admin. Different seats, same calm.",
  },
  {
    title: "Change the language.",
    body: "Look bottom-right or in the marketing nav — the language switcher exposes nine languages including Arabic (right-to-left). Try it.",
  },
];

const PARENT_STEPS = [
  {
    title: "Your child's day at a glance.",
    body: "The home tiles show what matters: who can pick up, today's photos, recent moments. Each tile is one calm click.",
    to: "/home",
  },
  {
    title: "Pickup, one tap.",
    body: "When you arrive at the center, a calm tap confirms the handover. No PIN, no QR codes, no friction.",
    to: "/pickup",
  },
  {
    title: "Today, in one paragraph.",
    body: "Open 'Today' to see a quiet timeline of the day — the kind a caregiver writes after a calm afternoon.",
    to: "/home",
  },
  {
    title: "Photos, kept softly.",
    body: "The gallery holds the small moments — paintings, naptime, garden walks. No streams. No surveillance.",
    to: "/moments",
  },
  {
    title: "Documents and the family handbook.",
    body: "Forms and policies live here. Searchable, downloadable, calm.",
    to: "/workspace",
  },
];

const CAREGIVER_STEPS = [
  {
    title: "The room at a glance.",
    body: "Each child appears as a calm tile with state (playing, resting, calm). One tap updates state — never alarming.",
    to: "/staff",
  },
  {
    title: "Health log.",
    body: "Add medications, note allergies, log administrations. Parents see only what they need.",
    to: "/staff/health",
  },
  {
    title: "Today's gallery.",
    body: "Share a moment — one photo, one sentence. Parents see the small things you noticed.",
    to: "/moments",
  },
];

const ADMIN_STEPS = [
  {
    title: "Demo requests inbox.",
    body: "Real inbound interest from real prospects (in this demo, also fictional). Triage gently.",
    to: "/admin/demo-requests",
  },
  {
    title: "Privacy & retention.",
    body: "Right-to-be-forgotten exports + retention policies, all auditable.",
    to: "/admin/privacy",
  },
  {
    title: "Audit logs.",
    body: "Every sensitive action is recorded. Filter by actor, action, severity.",
    to: "/admin/audit-logs",
  },
  {
    title: "Translation feedback.",
    body: "Visitors writing in nine languages tell you when copy feels off. You triage their suggestions here.",
    to: "/admin/translation-feedback",
  },
];

const FINAL_STEP = {
  title: "That's it.",
  body: "Tap any tile, switch persona, change language — the rest is exploration. If you want to talk to a human about your center, the demo CTA at the top still works.",
};

function stepsFor(role) {
  let extra = PARENT_STEPS;
  if (role === "caregiver") extra = CAREGIVER_STEPS;
  if (role === "admin") extra = ADMIN_STEPS;
  return [...COMMON_STEPS, ...extra, FINAL_STEP];
}

export default function GuidedTour({ open, userRole, onClose }) {
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();
  const steps = useMemo(() => stepsFor(userRole), [userRole]);

  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx]);

  if (!open) return null;
  const step = steps[idx];
  const isLast = idx === steps.length - 1;

  function next() {
    if (isLast) {
      onClose();
      return;
    }
    const ns = steps[idx + 1];
    if (ns.to) nav(ns.to);
    setIdx(idx + 1);
  }
  function prev() {
    if (idx === 0) return;
    setIdx(idx - 1);
  }

  return (
    <div
      data-testid="guided-tour"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guided-tour-title"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-[#0F2018]/45 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-white shadow-[0_24px_48px_rgba(31,58,42,0.32)] border border-[#EEF0F4] p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="guided-tour-close"
          aria-label="Close tour"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#6B7280] hover:text-[#1A1B2E] p-1.5"
        >
          <X size={18} />
        </button>

        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#1F3A2A]">
          Tour · {idx + 1} of {steps.length}
        </div>
        <h2
          id="guided-tour-title"
          data-testid="guided-tour-title"
          className="mt-3 font-display text-2xl tracking-tight text-[#1A1B2E]"
        >
          {step.title}
        </h2>
        <p
          data-testid="guided-tour-body"
          className="mt-3 text-sm text-[#6B7280] leading-relaxed"
        >
          {step.body}
        </p>

        {/* Progress dots */}
        <div className="mt-5 flex items-center gap-1.5" aria-hidden="true">
          {steps.map((_, i) => (
            <span
              key={i}
              className={
                "h-1.5 rounded-full transition-all " +
                (i === idx ? "w-6 bg-[#1F3A2A]" : "w-1.5 bg-[#EEF0F4]")
              }
            />
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            data-testid="guided-tour-prev"
            onClick={prev}
            disabled={idx === 0}
            className="text-sm text-[#6B7280] hover:text-[#1A1B2E] disabled:opacity-30 inline-flex items-center gap-1 px-3 py-2"
          >
            <ChevronLeft size={14} />
            Back
          </button>
          <button
            type="button"
            data-testid="guided-tour-next"
            onClick={next}
            className="btn-primary inline-flex items-center gap-1.5 !py-2 !px-5 !text-sm"
          >
            {isLast ? "Done" : "Next"}
            {!isLast && <ChevronRight size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}
